import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../Context/authContext";
import { Typography } from "../../Components/Typography";
import Card from "../../Components/Card";
import Avatar from "../../Components/Avatar";
import Button from "../../Components/Button";
import { getAccountContact } from "../../Utils/apiServices";
import { IdeasDonutChart, IDEAS_STAGES_DATA } from "../PartnerDashboards/shared";

const parseAccountContactResponse = (res) => {
  const body = res?.data;
  if (!body?.success) return { account: null, contacts: [] };
  const rawData = body?.data;
  if (Array.isArray(rawData) && rawData.length > 0) {
    const item = rawData[0];
    return {
      account: item?.account || null,
      contacts: Array.isArray(item?.contacts) ? item.contacts : [],
    };
  }
  if (rawData && typeof rawData === "object") {
    const contact = rawData?.contact || {};
    return {
      account: rawData?.account || null,
      contacts: [contact].filter((c) => c && (c.id || c.email)),
    };
  }
  return { account: null, contacts: [] };
};

const fundingAmountToTier = (amount) => {
  if (amount == null || amount === "" || Number.isNaN(Number(amount))) return "—";
  const n = Number(amount);
  if (n < 5000) return "Bronze";
  if (n < 15000) return "Silver";
  if (n < 25000) return "Gold";
  return "Platinum";
};

const CorporateSponsorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [accountInfo, setAccountInfo] = useState({
    partnerName: "",
    legalEntityName: "",
    website: "",
    tagline: "",
    companyLogo: null,
    fundingAmount: null,
    partnerTier: "—",
    reportFrequency: "",
    profileVisibility: false,
  });
  const [spocContacts, setSpocContacts] = useState([
    { name: "—", email: "—", phone: "—", role: "Partner & Investor", profilePictureUrl: null },
  ]);

  useEffect(() => {
    if (!user?.id) return;
    getAccountContact(user.id)
      .then((res) => {
        const { account, contacts } = parseAccountContactResponse(res);
        if (account) {
          const tier = fundingAmountToTier(account.funding_amount);
          setAccountInfo({
            partnerName: account.name || "Partner Name",
            legalEntityName: account.legal_entity_name || "",
            website: account.website || "partnername.com",
            tagline: account.tagline || "",
            companyLogo: account.company_logo || null,
            fundingAmount: account.funding_amount,
            partnerTier: tier,
            reportFrequency: account.report_frequency || "",
            profileVisibility: account.profile_visibility === true,
          });
        }
        if (contacts && contacts.length > 0) {
          setSpocContacts(
            contacts.map((c) => ({
              name: [c.first_name, c.last_name].filter(Boolean).join(" ").trim() || c.email || "—",
              email: c.email || "—",
              phone: c.phone || "—",
              role: c.company_role || "Partner & Investor",
              profilePictureUrl: c.profile_picture_url || null,
            }))
          );
        }
      })
      .catch(() => {});
  }, [user?.id]);

  return (
    <section className="flex flex-col w-full h-auto rounded-[40px] pt-8 pb-12 gap-8 bg-primary">
      {/* Partner header */}
      <div className="w-auto px-8">
        <Card className="rounded-2xl shadow-sm p-6" bodyClassName="p-0">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {accountInfo.companyLogo ? (
                <img
                  src={accountInfo.companyLogo}
                  alt=""
                  className="h-12 w-12 rounded-full object-cover bg-violet-100"
                />
              ) : (
                <div className="h-12 w-12 rounded-full bg-violet-100 flex items-center justify-center">
                  <Icon icon="lucide:building-2" className="h-6 w-6 text-violet-600" />
                </div>
              )}
              <div>
                <Typography as="p" className="text-md font-semibold text-primary-900">
                  {accountInfo.partnerName || "Partner Name"}
                </Typography>
                <Typography as="p" className="text-sm text-tertiary-600">
                  {accountInfo.website || "partnername.com"}
                </Typography>
                {accountInfo.tagline ? (
                  <Typography as="p" className="text-xs text-tertiary-500 mt-0.5">
                    {accountInfo.tagline}
                  </Typography>
                ) : null}
              </div>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <div>
                <Typography as="p" className="text-xs text-tertiary-600">Partner Tier</Typography>
                <Typography as="p" className="text-sm font-semibold text-primary-900">
                  {accountInfo.partnerTier}
                </Typography>
              </div>
              <div>
                <Typography as="p" className="text-xs text-tertiary-600">Subscription Expiration</Typography>
                <Typography as="p" className="text-sm font-semibold text-primary-900">—</Typography>
              </div>
              {accountInfo.reportFrequency ? (
                <div>
                  <Typography as="p" className="text-xs text-tertiary-600">Report Frequency</Typography>
                  <Typography as="p" className="text-sm font-semibold text-primary-900">
                    {accountInfo.reportFrequency}
                  </Typography>
                </div>
              ) : null}
              <Button variant="secondary" size="small" onClick={() => {}}>Renew</Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Stats grid */}
      <div className="w-auto px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="flex flex-col rounded-2xl shadow-sm p-6" bodyClassName="p-0 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <div className="h-12 w-12 rounded-full bg-brand-secondary flex items-center justify-center">
              <Icon icon="lucide:zap" className="h-6 w-6 text-brand-icon" />
            </div>
            <Typography as="p" className="text-md font-medium text-primary-900">Ventures Launched</Typography>
          </div>
          <Typography as="h3" className="text-3xl font-semibold text-primary-900">0</Typography>
          <Typography as="p" className="text-sm text-[#475467] font-medium">Till Date</Typography>
        </Card>

        <Card className="flex flex-col rounded-2xl shadow-sm p-6 md:col-span-2 lg:col-span-1" bodyClassName="p-0 flex flex-col gap-4">
          <Typography as="p" className="text-md font-medium text-primary-900">Ideas at Various Stages</Typography>
          <IdeasDonutChart data={IDEAS_STAGES_DATA} />
          <div className="flex items-end justify-between">
            <Typography as="p" className="text-xs text-[#475467] font-medium">Current</Typography>
            <button onClick={() => navigate("/ideas-review")} className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1">
              View Requests <Icon icon="lucide:arrow-right" className="h-4 w-4" />
            </button>
          </div>
        </Card>

        <Card className="flex flex-col rounded-2xl shadow-sm p-6 md:col-span-2 lg:col-span-1" bodyClassName="p-0 flex flex-col gap-4">
          <Typography as="p" className="text-md font-medium text-primary-900">Organization SPOC</Typography>
          <div className="flex flex-col gap-4">
            {spocContacts.map((c, i) => (
              <div key={i} className="flex items-center gap-3">
                <Avatar
                  src={c.profilePictureUrl}
                  firstName={c.name.split(" ")[0]}
                  lastName={c.name.split(" ").slice(1).join(" ") || ""}
                  email={c.email}
                  size="w-10 h-10"
                  className="rounded-full bg-slate-200"
                />
                <div className="flex-1 min-w-0">
                  <Typography as="p" className="text-sm font-medium text-primary-900">{c.name}</Typography>
                  <Typography as="p" className="text-xs text-tertiary-600">{c.email}</Typography>
                  <Typography as="p" className="text-xs text-tertiary-600">
                    {c.phone !== "—" ? `${c.phone} · ` : ""}{c.role}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Second row */}
      <div className="w-auto px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { title: "Ventures Ready to Pitch", value: 0, footer: "This Quarter" },
          { title: "Students on IgniteNext", value: 0, footer: "Till Date" },
          { title: "Number of Hackathons", value: 0, footer: "Till Date" },
          { title: "Number of Webinars", value: 0, footer: "Till Date" },
        ].map((item, id) => (
          <Card key={id} className="flex flex-col rounded-2xl shadow-sm p-6" bodyClassName="p-0 flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <div className="h-12 w-12 rounded-full bg-brand-secondary flex items-center justify-center">
                <Icon icon="lucide:zap" className="h-6 w-6 text-brand-icon" />
              </div>
              <Typography as="p" className="text-md font-medium text-primary-900">{item.title}</Typography>
            </div>
            <Typography as="h3" className="text-3xl font-semibold text-primary-900">{item.value}</Typography>
            <Typography as="p" className="text-sm text-[#475467] font-medium">{item.footer}</Typography>
          </Card>
        ))}
      </div>

      {/* Events card */}
      <div className="w-auto px-8">
        <Card className="flex flex-col rounded-2xl shadow-sm p-6 max-w-md" bodyClassName="p-0 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-12 w-12 rounded-full bg-warning-secondary flex items-center justify-center">
                <Icon icon="lucide:clock" className="h-6 w-6 text-[#252B37]" />
              </div>
              <Typography as="p" className="text-md font-medium text-primary-900">Events</Typography>
            </div>
          </div>
          <Typography as="h3" className="text-3xl font-semibold text-primary-900">0</Typography>
          <div className="flex items-end justify-between">
            <Typography as="p" className="text-sm text-[#475467] font-medium">Next 30 days</Typography>
            <button onClick={() => navigate("/mentor-events")} className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1">
              View Events <Icon icon="lucide:arrow-right" className="h-4 w-4" />
            </button>
          </div>
        </Card>
      </div>
    </section>
  );
};

export default CorporateSponsorDashboard;
